import React from "react";

import { RiDeleteBinLine, RiAddLine } from "react-icons/ri";
import { Oval } from "react-loader-spinner";

/**
 * Button component renders a styled button with different variants:
 * - primary -- Button with primary color background.
 * - secondary -- Button with secondary color background.
 * - disabled -- Button with disabled styling.
 * - link -- Button styled as a link.
 * - inline -- Button with inline styling.
 * - inlineLight -- Button with light inline styling.
 * - delete -- Button with delete icon styling.
 * - outline -- Button with outline styling.
 * - navPill -- Subtle translucent pill for buttons on dark navigation bars.
 * - warning -- Button with warning styling.
 *
 * @component
 *
 * @param {string} [variant='primary'] - The variant of the button.
 * @param {string} text - The text to be displayed inside the button.
 * @param {string} [icon] - The type of icon to be displayed inside the button.
 * @param {function} [onClick] - The function to be called when the button is clicked.
 * @param {string} [className] - Additional classes to be added to the button.
 * @param {boolean} [disabled=false] - Whether the button is disabled.
 * @param {object} [props] - Additional props to be passed to the button.
 * @param {React.Ref} [ref] - The ref to be attached to the button.
 * @example
 * return (
 *  <Button text="Add Job" variant="secondary" />
 * )
 */
const Button = React.forwardRef(
  ({ variant = "primary", text, icon, onClick, className, disabled = false, ...props }, ref) => {
    const variantClasses = {
      primary:
        "bg-primary text-white hover:bg-secondary-600 hover:text-gray-100 py-1.5 px-2 font-normal",
      secondary:
        "text-white bg-secondary hover:bg-secondary-500 hover:text-gray-100 py-1.5 px-2",
      navPill:
        "rounded-full border border-white/15 bg-white/10 px-3 py-1 text-sm font-medium text-white/80 hover:bg-white/20 hover:text-white",
      disabled:
        "text-white bg-gray-300 text-[#a0a7b3] cursor-not-allowed pointer-events-none py-1.5 px-2",
      cancel: "border border-gray-300 bg-white px-3 py-1.5 text-sm font-normal text-gray-700 hover:bg-gray-100",
      link: "text-green-600 cursor-not-allowed mx-2 py-1.5 px-2",
      inline:
        "text-white bg-[#0284c7] hover:text-gray-100 h-7 hover:bg-[#0091db] cursor-pointer py-auto w-full",
      inlineLight:
        "border-[1px] border-gray-300 text-gray-900 bg-gray-200 hover:text-black h-7 hover:bg-gray-300 cursor-pointer font-bold py-auto w-full",
      delete: "text-red-800 w-4 h-4 my-auto",
      deleteRed: "border border-red-300 bg-white px-3 py-1.5 text-sm font-normal text-red-700 hover:bg-red-500 hover:text-white",
      custom: "",
      none: "",
      outline: "outline-dashed outline-1",
      outlineBlue:
        "outline-dashed h-6 outline-blue-950 outline-1 bg-blue-100 hover:bg-blue-200 text-blue-900",
      outlineTransparent:
        "outline-dashed h-6 outline-gray-700 outline-1 bg-transparent hover:bg-gray-200 text-gray-900",
      faded:
        "outline-1 h-6 outline-emerald-700 outline-1 bg-emerald-950 bg-opacity-20 hover:bg-gray-200 text-gray-900",
      warning: "text-white bg-red-500 hover:bg-red-600 py-1.5 px-2",
      caution: "text-black bg-yellow-300 hover:bg-yellow-400 py-1.5 px-2",
      cautionLight: "text-black bg-yellow-100 hover:bg-yellow-200 py-1.5 px-2",
      pulseWarning:
        "text-white bg-red-500 hover:bg-red-600 py-1.5 px-2 animate-pulse",
      hidden: "hidden",
    };

    const iconType = {
      delete: <RiDeleteBinLine className="h-4" />,
      plus: <RiAddLine className="h-4" />,
      spinner: (
        <div className="flex w-full items-center justify-center p-auto">
          <Oval
            height={24}
            width={24}
            color="#b5b5b5"
            visible={true}
            ariaLabel="oval-loading"
            secondaryColor="#4d4d4d"
            strokeWidth={6}
            strokeWidthSecondary={8}
          />
        </div>
      ),
    };

    const classes = variantClasses[variant];
    const roundedClasses = classes?.includes("rounded") ? "" : "rounded-md";

    return (
      <div
        ref={ref}
        className={`${variant === "none" ? `${className}` : `${roundedClasses} caret-transparent
    cursor-pointer transition-all ease-in-out duration-300 text-center flex items-center justify-center gap-2 ${classes} ${className} ${disabled ? "cursor-not-allowed pointer-events-none opacity-50" : ""}`}`}
        onClick={onClick}
        {...props}
      >
        {icon && iconType[icon] ? iconType[icon] : icon}
        {text}
      </div>
    );
  }
);

export default Button;
